// Shared pieces for the Billing page and the cancel survey.

// Coaches whose clients can choose Premium (it includes a monthly session
// with the coach). The Subscribe modal uses the same list.
export const PREMIUM_COACHES = [
  'emilyblain',
  'kristinwade',
  'nicksmith',
  'zachwhelchel',
  'jonathanakerman',
];

export type CancelReason = { id: string; label: string };

export type BillingSummary = {
  source: 'stripe' | 'none';
  status?: string;
  isCurrent?: boolean;
  plan?: 'basic' | 'premium' | 'other';
  planName?: string | null;
  amount?: number | null;
  currency?: string;
  interval?: string;
  currentPeriodEnd?: number | null;
  cancelAtPeriodEnd?: boolean;
  cancelAt?: number | null;
  endedAt?: number | null;
  trialEnd?: number | null;
  pastDue?: boolean;
  paymentMethod?: { type: string; label: string; expires?: string } | null;
  invoices?: {
    id: string;
    date: number;
    amount: number;
    status: string;
    url: string | null;
  }[];
  prices: { basic: number; premium: number };
  cancelReasons: CancelReason[];
};

export function formatMoney(cents: number | null | undefined, currency = 'usd') {
  if (cents == null) return '';
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  });
}

export function formatDate(seconds: number | null | undefined) {
  if (!seconds) return '';
  return new Date(seconds * 1000).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function isInApp() {
  return Boolean(window.ReactNativeWebView);
}

// Subscriptions bought in the iPhone app can only be cancelled by Apple, so
// the app opens its own subscription screen.
export function openStoreSubscriptions() {
  window.ReactNativeWebView?.postMessage(
    JSON.stringify({ type: 'manage_subscription' }),
  );
}

export function planLabel(summary: BillingSummary) {
  if (summary.plan === 'premium') return 'Premium';
  if (summary.plan === 'basic') return 'Basic';
  return summary.planName || 'Your plan';
}

export function accessEnds(summary: BillingSummary) {
  return summary.cancelAt || summary.currentPeriodEnd;
}

export type LockReason = 'trial' | 'subscription' | 'sponsorship' | 'coach';

// Whether this member's access has ended, from their Airtable status (which
// the server now keeps in step with Stripe and Apple). Active coaches,
// sponsored and paying members are never locked. A coach account Kristin has
// closed (coach_account_expired) is.
export function accessLockReason(
  uiMode: string | null,
  status: string | null,
  trialEnds: string | null,
): LockReason | null {
  if (status === 'coach_account_expired') return 'coach';
  if (uiMode !== 'user') return null;

  if (status === 'free_trial_expired') return 'trial';
  if (status === 'free_trial' && trialEnds) {
    const ends = Date.parse(trialEnds);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (!Number.isNaN(ends) && ends < today.getTime()) return 'trial';
  }
  if (status === 'paid_expired' || status === 'premium_expired') {
    return 'subscription';
  }
  if (status === 'sponsored_expired') return 'sponsorship';
  return null;
}

export const ERROR_TEXT: Record<string, string> = {
  'reason-required': 'Please choose a reason.',
  'details-required': 'Please tell us a little more.',
  'payment-failed':
    "Your card was declined, so your plan hasn't changed. Update your card and try again.",
  'no-subscription': "We couldn't find your subscription.",
};

export function errorText(code: string | undefined) {
  return (
    (code && ERROR_TEXT[code]) ||
    'Something went wrong. Please try again, or email admin@mybudgetcoach.com.'
  );
}
